import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { SessionService } from '@agenda-customer-ui/data-access';
import { DateFormat, SchedulingModalityEnum, StorageKeys } from '@core/enums';
import { ICancelSchedule, IDriver, IOpenSchedule, IPayloadDriver, IPaymentMethod, IResponseModel } from '@core/interface';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { formatHourDefault } from '@core/utils';
import { API, environment } from '@core/envs';

@Injectable({
  providedIn: 'root'
})
export class NewScheduleService {
  protected readonly http = inject(HttpClient);
  protected readonly sessionService = inject(SessionService);
  protected readonly translate = inject(TranslateService);

  #modality = "DEALER";

  readonly #defaultPaymentMethod: IPaymentMethod = {
    online: false,
    gateway: 'Dealer',
    paymentId: null as unknown as number,
    status: 'dealer' as IPaymentMethod['status'],
    price: null as unknown as number,
    text: 'payment-methods.status.dealer',
    isRefund: null,
    paymentDate: null,
  };

  #normalizePaymentMethod(paymentMethod: unknown): IPaymentMethod[] {
    if (paymentMethod === null || paymentMethod === undefined) {
      return [{ ...this.#defaultPaymentMethod }];
    }

    if (Array.isArray(paymentMethod)) {
      return paymentMethod.length > 0 ? paymentMethod : [{ ...this.#defaultPaymentMethod }];
    }
    
    return [paymentMethod as IPaymentMethod];
  }

  getOpened(vin: string): Observable<IResponseModel<IOpenSchedule[]>> {
    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/list`;
    const country = this.sessionService.getItem(StorageKeys.COUNTRY);
    const  headers = {
      countryCode: country
    };
    return this.http.post<IResponseModel<IOpenSchedule[]>>(url, { vin, status: [1] }, { headers }).pipe(
      map(data => {
        data.response = data.response.map((schedule) => ({
          ...schedule,
          dateAndHour: `${this.formattedDate(schedule.scheduleDate)} - ${formatHourDefault(schedule.scheduleHour)}`,
          modality: this.#determineModality(schedule),
          paymentMethod: this.#normalizePaymentMethod(schedule.paymentMethod),
        }));
        return data;
      }),
      tap(data => { this.sessionService.setObject(StorageKeys.OPEN_SCHEDULES, data) })
    );
  }

  delete(
    schedule: IOpenSchedule,
    cancelUser: string,
    reschedule: boolean
  ): Observable<IResponseModel<null>> {
    const url = `${environment.getRoute(API.transformation)}/schedules`;

    const body: ICancelSchedule = {
      cancelUser,
      reschedule,
      division: schedule.divisionId,
      country: schedule.country,
      dealer: schedule.dealerId,
      advisor: schedule.advisorId,
      scheduleHour: schedule.scheduleHour,
      scheduleDate: dayjs(schedule.scheduleDate).format(DateFormat.ISO),
    };

    return this.http.delete<IResponseModel<null>>(url, { body }).pipe(
      tap((data) => console.log(data))
    );
  }

  getScheduleStatusData(vin: string, country: string, status: number): Observable<IResponseModel<Partial<IOpenSchedule[]>>> {
    const schedules = this.sessionService.getObject<IResponseModel<Partial<IOpenSchedule[]>>>(StorageKeys.STATUS_SCHEDULES);

    if (schedules.response?.length && vin === schedules?.response[0]?.vin) {
      return of(schedules);
    }

    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/status`;

    const  headers = {
      countryCode: country
    };

    return this.http.post<IResponseModel<Partial<IOpenSchedule[]>>>(url, { vin, status: [status] }, { headers }).pipe(
      tap(data => {
        this.sessionService.setObject(StorageKeys.STATUS_SCHEDULES, data);
      })
    );
  }

  getDriverSchedule(payload: IPayloadDriver): Observable<IResponseModel<IDriver>> {
    const url = `${environment.getRoute(API.scheduling)}/schedules/v2/drivers`;
    const scheduleDate = payload.date + 'T' + '00:00:00.000';

    const body = {
      vin: payload.vin,
      advisor: payload.advisor,
      date: scheduleDate,
      dealer: payload.dealer,
      hour: payload.hour,
    };

    const headers = {
      countryCode: payload.country
    };

    return this.http.post<IResponseModel<IDriver>>(url, body, { headers }).pipe(
      tap(data => {
        if (!data.response) {
          return;
        }

        data.response = {
          ...data.response,
          dateAndHour: this.#getDriverDateAndHour(data.response),
        };
      })
    );
  }

  #getDriverDateAndHour(driver: IDriver): string {
    if (!driver.checkoutDate || !driver.checkoutHour) {
      return this.translate.instant('dashboard.schedule.no-pud-hour');
    }

    return `${this.formattedDate(driver.checkoutDate)} - ${formatHourDefault(driver.checkoutHour)}`;
  }

  getImageDriverSchedule(fileName: string): Observable<string> {
    let url: string = environment.getRoute(API.dealermedia);
    url = url.concat('/images/get/advisor/v2');

    return this.http.post(url, { fileName }, { responseType: 'arraybuffer' }).pipe(
      map((response: ArrayBuffer) => {
        let binary = '';
        const bytes = new Uint8Array(response);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return 'data:image/png;base64,' + btoa(binary);
      })
    );
  }

  // Função auxiliar para determinar a modalidade do agendamento
  // alterar o nome para setModalityNewSchedule
  setModality(modality: string) {
    this.#modality = modality;
  }

  // alterar o nome para setModalityNewSchedule
  getModality() {
    return this.#modality;
  }

  formattedDate(date: string) {
    const utcDate = new Date(date);
    const weekday = utcDate.getUTCDay().toString();
    const formattedDate = `${utcDate.getUTCDate().toString().padStart(2, '0')}/${(utcDate.getUTCMonth() + 1).toString().padStart(2, '0')}/${utcDate.getUTCFullYear()}`;

    return `${this.translate.instant('weekdays.' + weekday)}, ${formattedDate}`;
  }

  // Função auxiliar para determinar a modalidade
  #determineModality(schedule: IOpenSchedule): SchedulingModalityEnum {
    if (schedule?.pickupDelivery) {
      return SchedulingModalityEnum.PDL;
    } else if (schedule?.winchService) {
      return SchedulingModalityEnum.WINCH;
    } else if (schedule?.mobileService) {
      return SchedulingModalityEnum.MSV;
    } else {
      return SchedulingModalityEnum.DEALER;
    }
  }
}
